import {cloneDeep, isEmpty, isString, isUndefined} from 'lodash'
import {BltIframeConstants} from '@/common/constant/BltIframeConstant'
import {$inj, $injByInterface} from '@/common/decorators/depinject'
import Base64Factory from '@/common/services/utils/Base64Factory'
import BltIframeFactory from '@/common/services/Iframe/bltIframeFactory'
import bltSignButton from '@/common/components/bltSignButton/bltSignButton.vue'
import type {DirectiveBinding} from 'vue'
import {createApp, type Directive, h, onMounted, ref} from 'vue'
import type IWorkspaceStore from '@/common/services/Workspace/IWorkspaceStore'
import type {DisclosureTag} from '@/common/directives/bltIframe/DisclosureTag'
import type {DisclosureDictionary} from '@/common/directives/bltIframe/DisclosureDictionary'
import type {BltIframeData} from '@/common/directives/bltIframe/BltIframeData'
import {createGuidedDisclosureController, type GuidedDisclosureController} from '@/common/directives/bltIframe/guidedDisclosure'
import {hasSignatureSigner, isSignatureSigned} from '@/common/directives/bltIframe/signatureUtils'

const defaultHtml =
  '' +
  '<html lang="en">' +
  '   <body>' +
  '       <div style="min-height:300px; width:100%;"></div>' +
  '   </body>' +
  '</html>'
const getIframeDoc = (el: HTMLIFrameElement): Document | null => {
  if (el.contentWindow?.document?.getElementById) {
    return el.contentWindow?.document
  } else if (el.contentDocument?.getElementById) {
    return el.contentDocument
  } else {
    return null
  }
}
const getIframeBody = (el: HTMLIFrameElement) => {
  const doc = getIframeDoc(el)
  return !isUndefined(doc) ? doc?.body : undefined
}

const escapeAttrValue = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')

const getTagElement = (iframeDoc: Document, tagName: string): HTMLElement | null => {
  const byId = iframeDoc.getElementById(tagName) as HTMLElement | null
  if (byId) {
    return byId
  }

  const escapedTagName = escapeAttrValue(tagName)
  const byBltTag = iframeDoc.querySelector(`[blt-tag="${escapedTagName}"]`) as HTMLElement | null
  if (byBltTag) {
    return byBltTag
  }

  return iframeDoc.querySelector(`[name="${escapedTagName}"]`) as HTMLElement | null
}

const getDictionary = (el: HTMLIFrameElement, dictionary: DisclosureDictionary): BltIframeData => {
  const dictionaryCopy: DisclosureDictionary = cloneDeep(dictionary)
  const tags: DisclosureTag[] = Object.keys(dictionaryCopy).map((tagKey) =>
    getValueFromHtml(el, dictionaryCopy[tagKey])
  )
  const populatedDictionary: DisclosureDictionary = tags.reduce((col: DisclosureDictionary, tag: DisclosureTag) => {
    col[tag.tagName] = tag
    return col
  }, {})
  return {dictionary: populatedDictionary}
}
const getValueFromHtml = (el: HTMLIFrameElement, tag: DisclosureTag) => {
  // const logService = $inj(LogService);
  const iframeDoc = getIframeDoc(el)
  const tagElem = iframeDoc ? getTagElement(iframeDoc, tag.tagName) : null
  if (tagElem === null) {
    return tag
  }
  switch (tag.tagType) {
    case BltIframeConstants.TEXTBOX:
      tag.value = (<HTMLTextAreaElement>tagElem)?.value
      break
    case BltIframeConstants.CHECKBOX:
      {
        const checked = (<HTMLInputElement>tagElem)?.checked
        // Preserve incoming value type to avoid sending unexpected types to disclosure APIs.
        tag.value = isString(tag.value) ? checked.toString() : checked
      }
      break
    case BltIframeConstants.SELECT:
      tag.value =
        -1 !== (<HTMLSelectElement>tagElem).selectedIndex
          ? (<HTMLOptionElement>(<HTMLSelectElement>tagElem)[(<HTMLSelectElement>tagElem).selectedIndex]).value
          : ''
      break
    case BltIframeConstants.SIGNATURE: {
      const signed = tagElem.getAttribute('signed') === 'true'
      tag.signed = signed.toString()
      // Only update the signed flag when there is an existing signature object.
      // Sending { signed: true } with no imageId causes a backend validation error.
      if (isString(tag.value) && tag.value.length > 0) {
        try {
          const parsedValue = JSON.parse(tag.value)
          if (parsedValue && typeof parsedValue === 'object') {
            parsedValue.signed = signed
            tag.value = JSON.stringify(parsedValue)
          }
        } catch {
          // Value is not valid JSON — leave tag.value unchanged
        }
      } else if (tag.value && typeof tag.value === 'object') {
        ;(tag.value as Record<string, unknown>).signed = signed
        tag.value = JSON.stringify(tag.value)
      }
      // else: no value present — do not synthesize a partial signature object
      break
    }
  }
  return tag
}
const guidedDisclosureControllers = new WeakMap<HTMLIFrameElement, GuidedDisclosureController>()

type IframeRenderSnapshot = {
  html: string
  dictionary: DisclosureDictionary
  disclosureStatus?: string
  guidedDisclosureEnabled?: boolean
  maxCardContentHeight?: number
  uuid?: string
  enrollmentId?: number
}

const iframeRenderSnapshots = new WeakMap<HTMLIFrameElement, IframeRenderSnapshot>()

const isGuidedEditableStatus = (disclosureStatus?: string) => {
  if (!isString(disclosureStatus)) {
    return true
  }

  const normalizedStatus = disclosureStatus.toUpperCase()
  return normalizedStatus !== 'COMPLETE' && normalizedStatus !== 'ACCEPTED'
}

const setupGuidedDisclosure = (
  iframe: HTMLIFrameElement,
  iframeDoc: Document,
  guidedDisclosureEnabled?: boolean,
  disclosureStatus?: string
) => {
  if (!guidedDisclosureEnabled) {
    destroyGuidedDisclosure(iframe)
    return
  }

  const existingController = guidedDisclosureControllers.get(iframe)
  if (existingController) {
    existingController.destroy()
  }

  const guidedController = createGuidedDisclosureController(iframe, iframeDoc, isGuidedEditableStatus(disclosureStatus))
  guidedDisclosureControllers.set(iframe, guidedController)
}

const destroyGuidedDisclosure = (iframe: HTMLIFrameElement) => {
  const existingController = guidedDisclosureControllers.get(iframe)
  if (existingController) {
    existingController.destroy()
  }
  guidedDisclosureControllers.delete(iframe)
}

const writeHtml = (
  iframe: HTMLIFrameElement,
  html: string,
  dictionary?: any,
  maxCardContentHeight?: number,
  guidedDisclosureEnabled?: boolean,
  disclosureStatus?: string
) => {
  const bltIframeFactory = $inj(BltIframeFactory)
  html = isString(html) ? html : defaultHtml
  const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
  if (iframeDoc == null) {
    return
  }
  iframeDoc.open()
  iframeDoc.write(html)
  iframeDoc.close()
  waitForIframeLoad(iframe).then(() => {
    const iframeDoc = getIframeDoc(iframe)
    if (!iframeDoc) {
      return
    }
    if (!isEmpty(dictionary)) {
      const tagMap = bltIframeFactory.getElementTagMap(iframe)
      injectSignatures(dictionary as any, iframeDoc, guidedDisclosureEnabled)
      injectSelect(iframeDoc, dictionary as any)
      bltIframeFactory.setTextboxValues(tagMap, dictionary as any)
      // Force a synchronous reflow so mobile browsers repaint the newly injected tag values
      // immediately instead of leaving them visually stale. Do not remove.
      void iframeDoc.body?.offsetHeight
    }
    if (maxCardContentHeight) {
      /**BLOCK OF CODE TO CALCULATE IFRAME CONTENT BODY HEIGHT and RESIZE IFRAME HEIGHT */
      const marginTop = parseInt(window.getComputedStyle(iframeDoc.body).getPropertyValue('margin-top'))
      const marginBottom = parseInt(window.getComputedStyle(iframeDoc.body).getPropertyValue('margin-bottom'))
      const paddingTop = parseInt(window.getComputedStyle(iframeDoc.body).getPropertyValue('padding-top'))
      const paddingBottom = parseInt(window.getComputedStyle(iframeDoc.body).getPropertyValue('padding-bottom'))
      const iframeBodyHeight = iframeDoc.body.scrollHeight + marginTop + marginBottom + paddingTop + paddingBottom
      //this block of code is being used only from ShowDisclosre type of screen
      if (iframeBodyHeight > maxCardContentHeight) {
        iframe.height = `${maxCardContentHeight}`
      } else {
        iframe.height = `${iframeBodyHeight + 35}`
      }
      const parentWrapperDiv = (iframe as HTMLIFrameElement).parentElement
      if (parentWrapperDiv) parentWrapperDiv.style.height = `${iframe.height}px`
    }

    setupGuidedDisclosure(iframe, iframeDoc, guidedDisclosureEnabled, disclosureStatus)
  })
}
const updateIframe = (
  dictionary: any,
  html: string,
  el: HTMLIFrameElement,
  uuid?: string,
  enrollmentId?: number,
  maxCardContentHeight?: number,
  guidedDisclosureEnabled?: boolean,
  disclosureStatus?: string
) => {
  if (!isEmpty(dictionary) && isString(html)) {
    processHtml(dictionary, html, el, uuid, enrollmentId).then((data: {html: string; dictionary: any}) => {
      writeHtml(el, data.html, data.dictionary, maxCardContentHeight, guidedDisclosureEnabled, disclosureStatus)
    })
  } else {
    writeHtml(el, html, null, maxCardContentHeight, guidedDisclosureEnabled, disclosureStatus)
  }
}
const waitForIframeLoad = (ele: HTMLIFrameElement) => {
  return new Promise<HTMLElement>((resolve) => {
    const timeInterval = setInterval(() => {
      const iframeBody = getIframeBody(ele)
      const iframeDoc = getIframeDoc(ele)
      if (iframeBody && iframeDoc) {
        clearInterval(timeInterval)
        resolve(iframeBody)
      }
    }, 20)
  })
}
const processHtml = (dictionary: any, html: string, content: HTMLIFrameElement, uuid?: string, enrollmentId?: number) => {
  const bltIframeFactory = $inj(BltIframeFactory)
  const dictionaryCloned = cloneDeep(dictionary),
    sortedDictionary = bltIframeFactory.sortDictionaryTags(dictionaryCloned)
  bltIframeFactory.getCheckboxReplacements(sortedDictionary)
  bltIframeFactory.getTextboxReplacements(sortedDictionary)
  bltIframeFactory.getSelectReplacements(sortedDictionary)
  bltIframeFactory.getSignatureReplacements(sortedDictionary)
  const shouldInjectImages = isString(uuid) && uuid.length > 0 && !isUndefined(enrollmentId)
  const injectSignatureImages = shouldInjectImages
    ? bltIframeFactory.injectApplicantImages(sortedDictionary, uuid, enrollmentId)
    : Promise.resolve()

  return injectSignatureImages.then(() => {
    const finalHTML = bltIframeFactory.replaceHtmlTags(html, sortedDictionary)
    return {html: finalHTML, dictionary: sortedDictionary}
  })
}
const waitForSignature = (
  signature: {
    originalTagName: string
  },
  content: any
): any => {
  // todo(mikol): We should just listen to the mounted signature component's lifecycle events, this is not necessary.
  // todo(mikol): Is it really necessary to query the document 150000 times?
  let timeInterval = 0
  return new Promise<any>((resolve, reject) => {
    const elemInterval = setInterval(() => {
      const signatureDiv = content.querySelectorAll('[' + CSS.escape(signature.originalTagName) + ']')[0]
      if (!isUndefined(signatureDiv)) {
        clearInterval(elemInterval)
        resolve(signatureDiv)
      }
      if (timeInterval / 5 === 30000) {
        clearInterval(elemInterval)
        reject()
      }
      timeInterval++
    }, 5)
  })
}
const injectSignatures = (
  dictionary: {
    signature: any[]
  },
  content: Document,
  guidedDisclosureEnabled?: boolean
) => {
  const base64Factory = $inj(Base64Factory)

  dictionary.signature.forEach((signature) => {
    const hasImageData = !isUndefined(signature.base64) && isString(signature.base64.imageDataBase64)
    const signatureData = hasImageData
      ? base64Factory.prefixBase64(signature.base64.imageDataBase64, signature.base64.imageFormat)
      : ''
    const shouldShowSignatureButton = hasSignatureSigner(signature)

    waitForSignature(signature, content).then(
      (signatureDiv: {setAttribute: (arg0: string, arg1: any) => void; appendChild: (arg0: any) => void}) => {
        signatureDiv.setAttribute('id', signature.tagName)
        if (!shouldShowSignatureButton) {
          if ((signatureDiv as Element).removeAttribute) {
            ;(signatureDiv as Element).removeAttribute('data-guided-signature')
          }
          if ((signatureDiv as HTMLElement).style) {
            ;(signatureDiv as HTMLElement).style.display = 'none'
          }
          return
        }

        if (signature.editable === false || signature.required === false) {
          if ((signatureDiv as Element).removeAttribute) {
            ;(signatureDiv as Element).removeAttribute('data-guided-signature')
          }
        } else {
          signatureDiv.setAttribute('data-guided-signature', 'true')
        }
        const EmbeddedSignatureApp = createApp({
          setup() {
            const initialSigned = isSignatureSigned(signature.value)
            const signed = ref(initialSigned)
            const setSignedAttribute = (value: boolean) => {
              signatureDiv.setAttribute('signed', value.toString())
            }

            onMounted(() => {
              setSignedAttribute(signed.value)
            })

            return () => {
              return h(bltSignButton, {
                editable: signature.editable,
                value: signed.value,
                data: signatureData,
                guidedDisclosureEnabled: !!guidedDisclosureEnabled,
                onInput: (value: boolean) => {
                  setSignedAttribute(value)
                  signed.value = value
                }
              })
            }
          }
        })
        const wrapper = document.createElement('div')
        EmbeddedSignatureApp.mount(wrapper)
        const getSignatureId = content.getElementById(signature.tagName)
        if (getSignatureId) {
          getSignatureId.appendChild(wrapper)
        }
      }
    )
  })
}
const injectSelect = (
  iframeDoc: Document,
  dictionary: {
    select: any[]
  }
) => {
  dictionary.select.forEach((tag) => {
    if (isString(tag.tagName) && isString(tag.value)) {
      const tagElem = iframeDoc.getElementById(tag.tagName) as HTMLSelectElement
      if (tagElem) {
        tagElem.value = tag.value
      } else {
        console.warn(`Tag ${tag.tagName} not found in iFrame`)
      }
    }
    if (isString(tag.tagName)) {
      const tagElem = iframeDoc.getElementById(tag.tagName) as HTMLSelectElement
      if (tagElem) {
        tagElem.disabled = !tag.editable
      } else {
        console.warn(`Tag ${tag.tagName} not found in iFrame`)
      }
    }
  })
}
interface bltIframeValues {
  html: string
  dictionary: DisclosureDictionary
  disclosureStatus?: string
  guidedDisclosureEnabled?: boolean
  maxCardContentHeight?: number
}

const shouldUpdateIframe = (
  iframe: HTMLIFrameElement,
  binding: DirectiveBinding<bltIframeValues>,
  uuid?: string,
  enrollmentId?: number
) => {
  const previousSnapshot = iframeRenderSnapshots.get(iframe)
  if (!previousSnapshot) {
    return true
  }

  return (
    binding.value.html !== previousSnapshot.html ||
    binding.value.dictionary !== previousSnapshot.dictionary ||
    binding.value.disclosureStatus !== previousSnapshot.disclosureStatus ||
    binding.value.guidedDisclosureEnabled !== previousSnapshot.guidedDisclosureEnabled ||
    binding.value.maxCardContentHeight !== previousSnapshot.maxCardContentHeight ||
    uuid !== previousSnapshot.uuid ||
    enrollmentId !== previousSnapshot.enrollmentId
  )
}

interface HTMLIframeTagElement extends HTMLIFrameElement {
  getDictionary(): BltIframeData
  goToNextRequired(): void
}
const bltIframe: Directive<HTMLIFrameElement, bltIframeValues> = {
  mounted: (el: HTMLIFrameElement, binding: DirectiveBinding<bltIframeValues>) => {
    const iframeEl = el as HTMLIframeTagElement
    iframeEl.getDictionary = () => getDictionary(iframeEl, binding.value.dictionary)
    iframeEl.goToNextRequired = () => {
      guidedDisclosureControllers.get(iframeEl)?.goToNextRequired()
    }
    const workspaceStore = $injByInterface<IWorkspaceStore>('IWorkspaceStore')
    const uuid = workspaceStore.workspaceUUID
    const enrollmentId = workspaceStore.enrollment.enrollmentId
    const {html, dictionary, maxCardContentHeight, disclosureStatus, guidedDisclosureEnabled} = binding.value
    updateIframe(
      dictionary,
      html,
      iframeEl,
      uuid,
      enrollmentId,
      maxCardContentHeight,
      guidedDisclosureEnabled,
      disclosureStatus
    )
    iframeRenderSnapshots.set(iframeEl, {
      html,
      dictionary,
      disclosureStatus,
      guidedDisclosureEnabled,
      maxCardContentHeight,
      uuid,
      enrollmentId
    })
  },
  updated(el: HTMLIFrameElement, binding: DirectiveBinding<bltIframeValues>) {
    const iframeEl = el as HTMLIframeTagElement
    const workspaceStore = $injByInterface<IWorkspaceStore>('IWorkspaceStore')
    const uuid = workspaceStore.workspaceUUID
    const enrollmentId = workspaceStore.enrollment.enrollmentId
    if (!shouldUpdateIframe(iframeEl, binding, uuid, enrollmentId)) {
      return
    }

    const {html, dictionary, maxCardContentHeight, disclosureStatus, guidedDisclosureEnabled} = binding.value
    updateIframe(
      dictionary,
      html,
      iframeEl,
      uuid,
      enrollmentId,
      maxCardContentHeight,
      guidedDisclosureEnabled,
      disclosureStatus
    )
    iframeRenderSnapshots.set(iframeEl, {
      html,
      dictionary,
      disclosureStatus,
      guidedDisclosureEnabled,
      maxCardContentHeight,
      uuid,
      enrollmentId
    })
  },
  unmounted(el: HTMLIFrameElement) {
    destroyGuidedDisclosure(el)
    iframeRenderSnapshots.delete(el)
  }
}
export default bltIframe
